if (!customElements.get('qr-navigation')) {
  class QRNavigation extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.media = window.matchMedia('(min-width: 1100px)');
      this.listen(this.media, 'change', () => this.resetForViewport());
      this.listen(window, 'pagehide', () => this.resetForViewport());
      if (this.dataset.mode === 'mobile') this.setupMobile();
      else this.setupDesktop();
    }

    disconnectedCallback() {
      clearTimeout(this.hoverTimer);
      clearTimeout(this.closeTimer);
      clearTimeout(this.focusTimer);
      this.controller?.abort();
      this.widthObserver?.disconnect();
      this.header?.removeAttribute('data-qr-compact');
      this.controller = null;
      if (this.dialog?.open) {
        this.dialog.close();
        document.documentElement.classList.remove('qr-nav-locked');
      }
    }

    listen(target, event, handler, options = {}) {
      target.addEventListener(event, handler, { ...options, signal: this.signal });
    }

    setupDesktop() {
      this.header = this.closest('#header-component');
      this.widthObserver = new ResizeObserver(([entry]) => {
        const width = entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width;
        const compact = width < 1100;
        if (this.header.hasAttribute('data-qr-compact') === compact) return;
        const menuHadFocus = this.contains(document.activeElement);
        this.header.toggleAttribute('data-qr-compact', compact);
        const mobile = this.header.querySelector('qr-navigation[data-mode="mobile"]');
        if (compact) {
          this.closeDesktop(false);
          if (menuHadFocus) mobile?.querySelector('[data-drawer-open]')?.focus({ preventScroll: true });
        } else {
          mobile?.resetForViewport();
        }
      });
      this.widthObserver.observe(this.header);
      this.disclosures = [...this.querySelectorAll('details')];
      this.disclosures.forEach((details) => {
        const trigger = details.querySelector('summary');
        this.listen(details, 'toggle', () => trigger.setAttribute('aria-expanded', String(details.open)));
        this.listen(trigger, 'click', (event) => {
          event.preventDefault();
          clearTimeout(this.hoverTimer);
          clearTimeout(this.closeTimer);
          if (this.active === details && this.reason !== 'hover') this.closeDesktop(false);
          else this.openDesktop(details, 'click');
        });
        this.listen(details, 'pointerenter', (event) => {
          if (event.pointerType !== 'mouse' || !window.matchMedia('(hover: hover)').matches) return;
          clearTimeout(this.closeTimer);
          clearTimeout(this.hoverTimer);
          this.hoverTimer = setTimeout(() => this.openDesktop(details, 'hover'), 100);
        });
        this.listen(details, 'pointerleave', () => {
          clearTimeout(this.hoverTimer);
          if (this.reason !== 'hover') return;
          this.closeTimer = setTimeout(() => this.closeDesktop(false), 220);
        });
        this.listen(trigger, 'keydown', (event) => {
          if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
          event.preventDefault();
          this.openDesktop(details, 'keyboard');
          const links = details.querySelectorAll('.qr-nav-panel a[href]');
          const next = event.key === 'ArrowDown' ? links[0] : links[links.length - 1];
          next?.focus();
        });
      });
      this.listen(document, 'pointerdown', (event) => {
        if (!this.contains(event.target)) this.closeDesktop(false);
      });
      this.listen(document, 'keydown', (event) => {
        if (event.key !== 'Escape' || !this.active) return;
        event.preventDefault();
        this.closeDesktop(true);
      });
      this.listen(this, 'focusout', () => {
        clearTimeout(this.focusTimer);
        this.focusTimer = setTimeout(() => {
          if (this.active && !this.active.contains(document.activeElement)) this.closeDesktop(false);
        }, 0);
      });
      this.listen(window, 'resize', () => this.closeDesktop(false), { passive: true });
    }

    openDesktop(details, reason) {
      if (!this.media.matches || !this.getClientRects().length) return;
      clearTimeout(this.closeTimer);
      if (this.active === details) {
        if (reason !== 'hover') this.reason = reason;
        return;
      }
      this.closeDesktop(false);
      details.open = true;
      details.querySelector('summary').setAttribute('aria-expanded', 'true');
      this.active = details;
      this.reason = reason;
    }

    closeDesktop(restoreFocus) {
      clearTimeout(this.hoverTimer);
      clearTimeout(this.closeTimer);
      if (!this.active) return;
      const trigger = this.active.querySelector('summary');
      this.active.open = false;
      trigger.setAttribute('aria-expanded', 'false');
      this.active = null;
      this.reason = null;
      if (restoreFocus) trigger.focus({ preventScroll: true });
    }

    setupMobile() {
      this.dialog = this.querySelector('dialog');
      this.opener = this.querySelector('[data-drawer-open]');
      this.home = this.querySelector('[data-menu-home]');
      this.groups = [...this.querySelectorAll('[data-menu-group]')];
      this.scroller = this.querySelector('.qr-nav-drawer-scroll');
      this.listen(this.opener, 'click', () => this.openDrawer());
      this.listen(this.querySelector('[data-drawer-close]'), 'click', () => this.closeDrawer());
      this.listen(this.dialog, 'cancel', (event) => {
        event.preventDefault();
        this.closeDrawer();
      });
      this.listen(this.dialog, 'close', () => this.finishClose());
      this.listen(this.dialog, 'keydown', (event) => {
        if (event.key !== 'Tab') return;
        const focusable = [...this.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
          .filter((element) => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden' && !element.closest('[hidden], [inert]'));
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first) return;
        if (event.shiftKey && (document.activeElement === first || !this.dialog.contains(document.activeElement))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !this.dialog.contains(document.activeElement))) {
          event.preventDefault();
          first.focus();
        }
      });
      this.listen(this.dialog, 'click', (event) => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.closeDrawer();
      });
      this.querySelectorAll('[data-open-group]').forEach((button) => {
        this.listen(button, 'click', () => this.openGroup(button));
      });
      this.querySelectorAll('[data-menu-back]').forEach((button) => {
        this.listen(button, 'click', () => this.showHome(true));
      });
      this.querySelectorAll('a[href]').forEach((link) => {
        this.listen(link, 'click', () => this.closeDrawer(false));
      });
    }

    openDrawer() {
      if (this.dialog.open) return;
      document.querySelectorAll('qr-navigation[data-mode="desktop"]').forEach((nav) => nav.closeDesktop(false));
      this.showHome(false);
      this.dialog.showModal();
      this.opener.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('qr-nav-locked');
      this.querySelector('[data-drawer-close]').focus({ preventScroll: true });
    }

    closeDrawer(restoreFocus = true) {
      if (!this.dialog?.open) return;
      this.restoreFocus = restoreFocus;
      this.dialog.close();
      this.finishClose();
    }

    finishClose() {
      if (this.opener.getAttribute('aria-expanded') !== 'true') return;
      document.documentElement.classList.remove('qr-nav-locked');
      this.opener.setAttribute('aria-expanded', 'false');
      if (this.restoreFocus !== false && this.opener.getClientRects().length) this.opener.focus({ preventScroll: true });
      this.restoreFocus = undefined;
      this.showHome(false);
    }

    openGroup(button) {
      const group = this.groups.find((item) => item.dataset.menuGroup === button.dataset.openGroup);
      if (!group) return;
      this.returnButton = button;
      this.home.hidden = true;
      this.groups.forEach((item) => { item.hidden = item !== group; });
      button.setAttribute('aria-expanded', 'true');
      this.scroller.scrollTop = 0;
      group.querySelector('[data-menu-back]').focus({ preventScroll: true });
    }

    showHome(restoreFocus) {
      this.home.hidden = false;
      this.groups.forEach((group) => { group.hidden = true; });
      this.querySelectorAll('[data-open-group]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      this.scroller.scrollTop = 0;
      if (restoreFocus) this.returnButton?.focus({ preventScroll: true });
    }

    resetForViewport() {
      if (this.dataset.mode === 'desktop') this.closeDesktop(false);
      else if (this.dialog?.open && !this.opener.getClientRects().length) {
        this.closeDrawer(false);
        document.querySelector('.qr-nav-desktop summary')?.focus({ preventScroll: true });
      }
    }
  }
  customElements.define('qr-navigation', QRNavigation);
}
